import { LLM_GENERATION_TASK_IDS } from "@/llm-generation/protocol"
import { z } from "zod"

export const DASHBOARD_DRILLDOWN_INSIGHTS_STREAM_TASK_ID =
  LLM_GENERATION_TASK_IDS.DASHBOARD_DRILLDOWN_INSIGHTS_STREAM

export const DASHBOARD_DRILLDOWN_INSIGHTS_MAX_INPUT_ROWS = 200
const DASHBOARD_DRILLDOWN_INSIGHTS_MAX_INSIGHTS = 3
const DASHBOARD_DRILLDOWN_INSIGHTS_DEFAULT_CONFIDENCE = "low"

const dashboardInsightValueSchema = z.union([z.number(), z.string()])
const dashboardDrilldownInsightConfidenceSchema = z.preprocess(
  (value) => {
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase()
      if (
        normalized === "high" ||
        normalized === "medium" ||
        normalized === "low"
      ) {
        return normalized
      }
    }

    return DASHBOARD_DRILLDOWN_INSIGHTS_DEFAULT_CONFIDENCE
  },
  z.enum(["high", "medium", "low"]),
)

const dashboardDrilldownInsightChartSchema = z.object({
  drillDownScope: z.enum(["chart", "point"]).optional(),
  dashboardId: z.string().optional(),
  widgetId: z.string().optional(),
  title: z.string().optional(),
  metricId: z.string().min(1),
  dataSource: z.string().min(1),
  aggregation: z.string().min(1),
  metricOutputType: z.string().min(1),
  bucketTime: z.string().min(1),
  endDate: z.string().optional(),
  bucketInterval: z.string().optional(),
  splitBy: z.string().optional(),
  splitValue: z.string().optional(),
  stringValue: z.string().optional(),
  aggregatedValue: dashboardInsightValueSchema,
  displayLabel: z.string().min(1),
  timezone: z.string().optional(),
})

export const dashboardDrilldownInsightRowSchema = z.object({
  simulationOutputId: z.string().min(1),
  runId: z.string().min(1),
  occurredAt: z.string().min(1),
  createdAt: z.string().optional(),
  value: dashboardInsightValueSchema,
  metricId: z.string().min(1),
  agentId: z.string().optional(),
  personaId: z.string().optional(),
  templateName: z.string().optional(),
  testSetId: z.string().optional(),
})

export const dashboardDrilldownInsightsInputSchema = z.object({
  chart: dashboardDrilldownInsightChartSchema,
  rows: z
    .array(dashboardDrilldownInsightRowSchema)
    .max(DASHBOARD_DRILLDOWN_INSIGHTS_MAX_INPUT_ROWS),
})

export const dashboardDrilldownInsightsStatusSchema = z.object({
  stepId: z.string().min(1),
  label: z.string().min(1),
  state: z.enum(["loading", "complete", "error"]),
})

const dashboardDrilldownInsightsEvidenceLinkSchema = z.object({
  rowRef: z.string().min(1),
  label: z.string().optional(),
  reason: z.string().optional(),
})

export const dashboardDrilldownInsightsOutputSchema = z.object({
  headline: z.string().min(1),
  summary: z.string().min(1),
  insights: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().min(1),
        category: z.string().min(1),
        confidence: dashboardDrilldownInsightConfidenceSchema,
        evidenceLinks: z.array(dashboardDrilldownInsightsEvidenceLinkSchema),
      }),
    )
    .max(DASHBOARD_DRILLDOWN_INSIGHTS_MAX_INSIGHTS),
  limitations: z.array(z.string()).optional(),
})

export type DashboardDrilldownInsightsInput = z.infer<
  typeof dashboardDrilldownInsightsInputSchema
>

export type DashboardDrilldownInsightRow = z.infer<
  typeof dashboardDrilldownInsightRowSchema
>

export type DashboardDrilldownInsightsOutput = z.infer<
  typeof dashboardDrilldownInsightsOutputSchema
>

export type DashboardDrilldownInsightsStatus = z.infer<
  typeof dashboardDrilldownInsightsStatusSchema
>
